import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  archiveCampaign,
  unarchiveCampaign,
  deleteCampaign,
  restoreCampaign,
  copyCampaign,
  createTemplate,
  createCampaignPreview,
  createCampaign,
} from './data';
import { useNotification } from '../../../../../shared/Notification/useNotification';

export const useArchiveCampaignMutation = () => {
  const { t } = useTranslation('campaigns');
  const queryClient = useQueryClient();
  const { showSuccess } = useNotification();

  const { mutate: archiveCampaignMutation } = useMutation(archiveCampaign, {
    onSuccess: () => {
      showSuccess({
        content: t('campaignNotifications.archived'),
      });

      queryClient.invalidateQueries(['campaigns']);
      queryClient.invalidateQueries(['campaigns-by-year']);
    },
  });

  return { archiveCampaignMutation };
};

export const useUnarchiveCampaignMutation = ({ onSuccessCallback } = {}) => {
  const { t } = useTranslation('campaigns');
  const queryClient = useQueryClient();
  const { showSuccess } = useNotification();

  const { mutate: unarchiveCampaignMutation } = useMutation(unarchiveCampaign, {
    onSuccess: () => {
      showSuccess({
        content: t('campaignNotifications.unarchived'),
      });

      queryClient.invalidateQueries(['campaigns']);
      queryClient.invalidateQueries(['campaigns-by-year']);

      if (onSuccessCallback) {
        onSuccessCallback();
      }
    },
  });

  return { unarchiveCampaignMutation };
};

export const useDeleteCampaignMutation = () => {
  const { t } = useTranslation('campaigns');
  const queryClient = useQueryClient();
  const { showSuccess } = useNotification();

  const { mutate: deleteCampaignMutation } = useMutation(deleteCampaign, {
    onSuccess: () => {
      showSuccess({
        content: t('campaignNotifications.deleted'),
      });

      queryClient.invalidateQueries(['campaigns']);
      queryClient.invalidateQueries(['campaigns-by-year']);
    },
  });

  return { deleteCampaignMutation };
};

export const useRestoreCampaignMutation = ({ onSuccessCallback } = {}) => {
  const { t } = useTranslation('campaigns');
  const queryClient = useQueryClient();
  const { showSuccess } = useNotification();

  const { mutate: restoreCampaignMutation } = useMutation(restoreCampaign, {
    onSuccess: () => {
      showSuccess({
        content: t('campaignNotifications.restored'),
      });

      queryClient.invalidateQueries(['campaigns']);
      queryClient.invalidateQueries(['campaigns-by-year']);

      if (onSuccessCallback) {
        onSuccessCallback();
      }
    },
  });

  return { restoreCampaignMutation };
};

export const useCopyCampaignMutation = () => {
  const { t } = useTranslation('campaigns');
  const { showDefault } = useNotification();
  const queryClient = useQueryClient();

  const { mutate: copyCampaignMutation } = useMutation(
    ({ uuid, campaignData }) => copyCampaign(uuid, campaignData),
    {
      onSuccess: () => {
        showDefault({
          content: t('campaignNotifications.pending'),
        });
        queryClient.invalidateQueries(['campaigns']);
        queryClient.invalidateQueries(['campaigns-by-year']);
      },
    },
  );

  return { copyCampaignMutation };
};

export const useCreateTemplateMutation = () => {
  const { t } = useTranslation('campaigns');
  const { showDefault } = useNotification();

  const { mutate: createTemplateMutation } = useMutation(createTemplate, {
    onSuccess: () => {
      showDefault({
        content: t('campaignNotifications.pendingTemplate'),
      });
    },
  });

  return { createTemplateMutation };
};

export const useCreateCampaignPreviewMutation = () => {
  const { mutate: createCampaignPreviewMutation } = useMutation(
    createCampaignPreview,
  );

  return { createCampaignPreviewMutation };
};

export const useCreateCampaignMutation = () => {
  const { t } = useTranslation('campaigns');
  const { showSuccess } = useNotification();
  const queryClient = useQueryClient();

  const { mutate: createCampaignMutation } = useMutation(createCampaign, {
    onSuccess: () => {
      showSuccess({
        content: t('campaignNotifications.created'),
      });
      queryClient.invalidateQueries(['campaigns']);
      queryClient.invalidateQueries(['campaigns-by-year']);
      queryClient.invalidateQueries(['plans']);
    },
  });

  return { createCampaignMutation };
};
